/**
 * Reels — TikTok-style vertical video wall
 */
(function () {
  'use strict';

  const API_URL       = window.__REELS_API__ || '';
  const BATCH_SIZE    = 10;
  const PRELOAD_AHEAD = 2;
  const FETCH_AHEAD   = 7;

  // Mapped from live DB product types → taminzo.com collection handles
  const TYPE_COLLECTION = {
    'stitched'         : 'stitched-women',      // assumed women — confirm?
    'unstitched'       : 'unstitched-women',     // assumed women — confirm?
    'jewellery'        : 'jewelry',
    'western wear'     : 'western-wear-men',     // assumed men — confirm?
    'eastern wear'     : 'eastern-wear-kids',    // assumed kids — confirm?
    'outer wear'       : 'outer-wear-women',     // assumed women — confirm?
    'active wear'      : 'active-wear-kids',     // assumed kids — confirm?
    'bedding'          : 'bedding',
    'loafers'          : 'loafers-men',
    'scarf / dupatta'  : 'women-scarves',
    'inner wear'       : 'inner-wear-kids',
    'home decor'       : 'home-decor',
    'modest wear'      : 'modest-wear-women',
    'hair accessories' : 'hair-accessories-women',
    'bags'             : 'bags-women',
    'sandals'          : 'sandals-women',
    'slides'           : 'slides-women',
    'homewear'         : 'homewear-men',
    'formal shoes'     : 'formal-shoes-men',
    'watches'          : 'watches-women',
    'socks'            : 'socks',
    'baby accessories' : 'baby-accessories',
    'sports shoes'     : 'sports-shoes-kids',
    'sneakers'         : 'sneakers-kids',
    // No collection found for these — will fall back to /collections/all:
    // 'wallets', 'night wear', 'caps', 'stationery'
  };

  function typeToCollectionUrl(productType) {
    if (!productType) return null;
    const key    = productType.toLowerCase().trim();
    const handle = TYPE_COLLECTION[key];
    return handle ? `/collections/${handle}` : null; // null = hide category button
  }

  // ─── State ────────────────────────────────────────────────────────────────
  const state = {
    videos     : [],
    currentIdx : 0,
    loading    : false,
    exhausted  : false,
    muted      : true,
  };

  // ─── Persistence ──────────────────────────────────────────────────────────
  function getReelsCountry() {
    return window.__REELS_COUNTRY__ || 'PK';
  }
  function getUserId() {
    try {
      let uid = localStorage.getItem('reels_uid');
      if (!uid) {
        uid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('reels_uid', uid);
      }
      return uid;
    } catch { return ''; }
  }
  function getBrandAffinities() {
    try { return JSON.parse(localStorage.getItem('reels_brands') || '{}'); } catch { return {}; }
  }
  function recordBrandView(name) {
    const aff = getBrandAffinities();
    aff[name] = (aff[name] || 0) + 1;
    try { localStorage.setItem('reels_brands', JSON.stringify(aff)); } catch {}
  }
  function topBrands(n = 5) {
    return Object.entries(getBrandAffinities())
      .sort((a, b) => b[1] - a[1]).slice(0, n).map(([n]) => n);
  }

  // ─── Analytics ────────────────────────────────────────────────────────────
  function trackEvent(name, props) {
    var clientType = window.isTaminzoApp === 'ios' ? 'ios_app'
                   : window.isTaminzoApp === 'android' ? 'android_app'
                   : 'web';
    var enriched = Object.assign({ client_type: clientType }, props);
    try { if (typeof gtag === 'function') gtag('event', name, enriched); } catch {}
    try {
      if (window.__statsigClient && typeof window.__statsigClient.logEvent === 'function')
        window.__statsigClient.logEvent(name, enriched.product_handle || enriched.brand || '', enriched);
    } catch {}
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function escHtml(s) {
    return String(s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;')
      .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }
  function formatPrice(p) {
    if (!p && p !== 0) return '';
    const active    = window.Shopify?.currency?.active || 'PKR';
    const rate      = parseFloat(window.Shopify?.currency?.rate || 1);
    const converted = p * rate;
    if (active === 'PKR') {
      return 'PKR ' + Math.round(converted).toLocaleString('en-PK');
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: active, maximumFractionDigits: 2,
      }).format(converted);
    } catch {
      return active + ' ' + converted.toFixed(2);
    }
  }
  function discountPct(price, compare) {
    if (!compare || compare <= price) return 0;
    return Math.round((1 - price / compare) * 100);
  }

  // ─── API ──────────────────────────────────────────────────────────────────
  async function fetchOnce(body) {
    const opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      ...(AbortSignal.timeout ? { signal: AbortSignal.timeout(25000) } : {}),
    };
    const res = await fetch(API_URL, opts);
    if (!res.ok) throw new Error('API ' + res.status);
    return res.json();
  }

  const CACHE_KEY         = 'reels_v2_cache';
  const CACHE_COUNTRY_KEY = 'reels_v2_country';
  // Bust cache when currency/market changes so uplifted prices are re-fetched
  try {
    const cachedCountry = sessionStorage.getItem(CACHE_COUNTRY_KEY);
    const currentCountry = getReelsCountry();
    if (cachedCountry && cachedCountry !== currentCountry) {
      sessionStorage.removeItem(CACHE_KEY);
    }
    sessionStorage.setItem(CACHE_COUNTRY_KEY, currentCountry);
  } catch {}

  async function fetchBatch() {
    if (state.loading || state.exhausted) return;
    state.loading = true;

    try {
      let data;

      if (state.videos.length === 0) {
        // Fast path 1: session cache — instant on repeat visits
        try {
          const raw = sessionStorage.getItem(CACHE_KEY);
          if (raw) {
            const cached = JSON.parse(raw);
            if (cached?.videos?.length) {
              data = cached;
              // Refresh cache in background for next visit
              fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ count: BATCH_SIZE, userId: getUserId(), country: getReelsCountry() }),
              })
                .then(r => r.json())
                .then(d => { if (d?.videos?.length) try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(d)); } catch {} })
                .catch(() => {});
            }
          }
        } catch {}

        // Fast path 2: prefetch promise started by inline <script> on page load
        if (!data) {
          try {
            const prefetch = window.__REELS_PREFETCH__;
            window.__REELS_PREFETCH__ = null;
            if (prefetch) {
              const result = await prefetch;
              if (result?.videos?.length) {
                data = result;
                try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
              }
            }
          } catch {}
        }
      }

      // Always fetch fresh if no data yet
      if (!data) {
        const body = { count: BATCH_SIZE, userId: getUserId(), brands: topBrands(), country: getReelsCountry() };
        try { data = await fetchOnce(body); }
        catch {
          await new Promise(r => setTimeout(r, 3000));
          data = await fetchOnce(body);
        }
        if (state.videos.length === 0 && data?.videos?.length) {
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
        }
      }

      const batch = data?.videos || [];
      if (!batch.length) {
        state.exhausted = true;
        if (!state.videos.length) renderEmpty();
        return;
      }
      // Aggressively prefetch first video the moment we have the URL —
      // before DOM is built, before activateItem, as early as possible.
      const isFirstBatch = state.videos.length === 0;
      if (isFirstBatch && batch[0]?.videoUrl) preloadVideoUrl(batch[0].videoUrl, 0);
      batch.forEach(v => state.videos.push(v));
      renderNewItems(batch);
    } catch (err) {
      console.error('[Reels]', err);
      if (!state.videos.length) renderError();
    } finally {
      state.loading = false;
    }
  }

  // ─── Single shared video player ───────────────────────────────────────────
  // One <video> element reused for every reel. iOS grants audio authorization
  // to the element object — once the user unmutes (gesture), that authorization
  // persists across src changes for the lifetime of the page. Per-item elements
  // only hold poster images and UI; this element handles all actual playback.
  const videoPlayer = document.createElement('video');
  videoPlayer.loop        = true;
  videoPlayer.muted       = true;
  videoPlayer.playsInline = true;
  videoPlayer.setAttribute('playsinline', '');
  videoPlayer.setAttribute('webkit-playsinline', '');
  // Styled to fill whichever reel item it's moved into
  videoPlayer.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none;';

  // ─── DOM refs ─────────────────────────────────────────────────────────────
  const container  = document.getElementById('reels-container');
  const initLoader = document.getElementById('reels-initial-loader');
  const pdpSheet   = document.getElementById('reels-pdp-sheet');
  const pdpContent = document.getElementById('reels-pdp-content');
  const pdpOverlay = document.getElementById('reels-pdp-overlay');
  const pdpPanel   = document.getElementById('reels-pdp-panel');
  const pdpClose   = document.getElementById('reels-pdp-close');

  // ─── Mute ─────────────────────────────────────────────────────────────────
  const ICON_MUTED   = `<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"/></svg>`;
  const ICON_UNMUTED = `<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>`;

  // Brand icon — exact storefront icon used in Taminzo bottom nav
  const ICON_BRAND = `<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.13238 1C4.07859 1 3.10207 1.5529 2.5599 2.45651L0.615776 5.69672C0.17816 6.42608 0.0121122 7.42549 0.508798 8.32014C0.789678 8.82607 1.27459 9.55181 2 10.1205V20C2 21.6569 3.34315 23 5 23H8C9.10457 23 10 22.1046 10 21V15H14V21C14 22.1046 14.8954 23 16 23H19C20.6569 23 22 21.6569 22 20V10.1205C22.7254 9.55181 23.2103 8.82607 23.4912 8.32014C23.9879 7.42548 23.8218 6.42608 23.3842 5.69672L21.4401 2.45651C20.8979 1.5529 19.9214 1 18.8676 1H5.13238ZM20 10.9697C19.8391 10.9895 19.6725 11 19.5 11C18.1259 11 17.1126 10.3216 16.4364 9.60481C16.2632 9.4211 16.1082 9.23119 15.9705 9.04325C15.2167 9.98812 13.9542 11 12 11C10.0458 11 8.7833 9.98812 8.02952 9.04325C7.89183 9.23119 7.73684 9.4211 7.56355 9.60481C6.8874 10.3216 5.87405 11 4.5 11C4.32752 11 4.16089 10.9895 4 10.9697V20C4 20.5523 4.44772 21 5 21H8V15C8 13.8954 8.89543 13 10 13H14C15.1046 13 16 13.8954 16 15V21H19C19.5523 21 20 20.5523 20 20V10.9697ZM4.27489 3.4855C4.45561 3.1843 4.78112 3 5.13238 3H18.8676C19.2189 3 19.5444 3.1843 19.7251 3.4855L21.6692 6.72571C21.8324 6.99765 21.8127 7.2231 21.7426 7.34937C21.2851 8.17345 20.5493 9 19.5 9C18.8448 9 18.323 8.69006 17.8913 8.23245C17.4506 7.76524 17.1659 7.20393 17.0284 6.88399C16.8114 6.37951 16.3329 6.21388 16.0033 6.21248C15.674 6.21109 15.1982 6.37172 14.9752 6.8683C14.6702 7.54754 13.7982 9 12 9C10.2018 9 9.32978 7.54754 9.0248 6.8683C8.80182 6.37172 8.32598 6.21109 7.99667 6.21248C7.66706 6.21388 7.18855 6.37951 6.97164 6.88399C6.83407 7.20393 6.5494 7.76524 6.10869 8.23245C5.67703 8.69006 5.15524 9 4.5 9C3.45065 9 2.71491 8.17345 2.2574 7.34937C2.1873 7.2231 2.1676 6.99765 2.33076 6.72571L4.27489 3.4855Z"/></svg>`;
  const ICON_SHARE = `<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>`;

  function setMuted(muted) {
    state.muted = muted;
    container.querySelectorAll('[data-action="mute"] .reels-action-btn__icon')
      .forEach(el => { el.innerHTML = muted ? ICON_MUTED : ICON_UNMUTED; });
    // videoPlayer.muted is set directly — on unmute this runs inside a tap gesture
    // so iOS authorizes the element. That authorization persists across src changes.
    videoPlayer.muted = muted;
  }

  // ─── Build item ───────────────────────────────────────────────────────────
  function buildItem(video, index) {
    const item = document.createElement('div');
    item.className     = 'reels-item';
    item.dataset.index  = index;
    item.dataset.id     = video.id;
    item.dataset.handle = video.product.handle;
    item.dataset.brand  = video.brand.name;
    if (video.productType) item.dataset.productType = video.productType;
    if (video.product.id) item.dataset.shopifyProductId = video.product.id;

    // No per-item <video> — videoPlayer (single shared element) handles all playback.
    // Explicit poster image — more reliable than vid.poster on mobile when preload changes
    if (video.thumbnailUrl) {
      const posterEl = document.createElement('img');
      posterEl.className = 'reel-poster';
      posterEl.src = video.thumbnailUrl;
      posterEl.alt = '';
      posterEl.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;pointer-events:none;';
      item.appendChild(posterEl);
    }

    const overlay = document.createElement('div');
    overlay.className = 'reels-item__overlay';
    item.appendChild(overlay);

    const flash = document.createElement('div');
    flash.className = 'reels-play-flash';
    item.appendChild(flash);

    // Right action buttons: Mute, Share, Brand
    const actions = document.createElement('div');
    actions.className = 'reels-item__actions';
    actions.innerHTML = `
      <button class="reels-action-btn" data-action="mute" aria-label="Toggle mute">
        <div class="reels-action-btn__icon">${state.muted ? ICON_MUTED : ICON_UNMUTED}</div>
        <span>${state.muted ? 'Unmute' : 'Mute'}</span>
      </button>
      <button class="reels-action-btn" data-action="share" aria-label="Share">
        <div class="reels-action-btn__icon">${ICON_SHARE}</div>
        <span>Share</span>
      </button>
      <button class="reels-action-btn" data-action="brand" aria-label="Brand">
        <div class="reels-action-btn__icon">${ICON_BRAND}</div>
        <span>Brand</span>
      </button>
    `;
    item.appendChild(actions);

    // Bottom product card
    const disc = discountPct(video.product.price, video.product.compareAtPrice);
    const card = document.createElement('div');
    card.className       = 'reels-item__card';
    card.dataset.action  = 'shop';
    card.innerHTML = `
      <div class="reels-card__thumb">
        ${video.thumbnailUrl ? `<img src="${escHtml(video.thumbnailUrl)}" alt="" loading="lazy">` : ''}
      </div>
      <div class="reels-card__info">
        <p class="reels-card__brand">${escHtml(video.brand.name)}</p>
        <p class="reels-card__price">
          <span class="reels-card__price-now">${formatPrice(video.product.price)}</span>
          ${video.product.compareAtPrice ? `<span class="reels-card__price-was">${formatPrice(video.product.compareAtPrice)}</span>` : ''}
          ${disc > 0 ? `<span class="reels-card__badge">-${disc}%</span>` : ''}
        </p>
      </div>
      <div class="reels-card__cta">Shop Now</div>
    `;
    item.appendChild(card);

    return item;
  }

  // ─── Render helpers ───────────────────────────────────────────────────────
  let bottomLoader = null;

  function showBottomLoader() {
    if (bottomLoader) return;
    bottomLoader = document.createElement('div');
    bottomLoader.className = 'reels-item reels-bottom-loader';
    bottomLoader.innerHTML = '<svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg"><circle cx="17" cy="17" r="14" fill="none" stroke="rgba(255,255,255,0.2)" stroke-width="3"/><circle cx="17" cy="17" r="14" fill="none" stroke="#fff" stroke-width="3" stroke-dasharray="22 66" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" dur="0.7s" from="0 17 17" to="360 17 17" repeatCount="indefinite"/></circle></svg>';
    container.appendChild(bottomLoader);
  }
  function removeBottomLoader() {
    if (bottomLoader) { bottomLoader.remove(); bottomLoader = null; }
  }

  function renderNewItems(batch) {
    if (initLoader) initLoader.remove();
    removeBottomLoader();
    const startIndex   = state.videos.length - batch.length;
    const isFirstBatch = startIndex === 0;

    container.appendChild(buildItem(batch[0], startIndex));
    if (isFirstBatch) activateItem(0);

    // Append remaining items in rAF so their poster images load in parallel
    // while the first video buffers — no video src is assigned until activated.
    if (batch.length > 1) {
      requestAnimationFrame(() => {
        for (let i = 1; i < batch.length; i++) {
          container.appendChild(buildItem(batch[i], startIndex + i));
        }
      });
    }
  }

  function renderEmpty() {
    if (initLoader) initLoader.remove();
    const el = document.createElement('div');
    el.className = 'reels-item reels-end-card';
    el.innerHTML = '<p>No videos yet.</p>';
    container.appendChild(el);
  }
  function renderError() {
    if (initLoader) initLoader.remove();
    const el = document.createElement('div');
    el.className = 'reels-item reels-end-card';
    el.innerHTML = `<p>Could not load videos.</p><button onclick="location.reload()">Retry</button>`;
    container.appendChild(el);
  }

  // ─── Playback ─────────────────────────────────────────────────────────────
  function getItemEl(i) { return container.querySelector(`.reels-item[data-index="${i}"]`); }
  function getVideoEl(i) { return i === state.currentIdx ? videoPlayer : null; }

  // Partial-fetch preloader — downloads the first 1 MB of upcoming videos into
  // the HTTP cache. iOS Safari 15.4+ shares this cache with video element requests
  // so the moov atom + initial frames are served instantly when the player loads.
  // Sequential (one at a time) so it never competes with the currently playing video.
  const _preloadedUrls = new Set();
  const _preloadQueue  = [];
  let   _preloadBusy   = false;

  // limit = 0 means download the full video (aggressive mode for first video)
  function preloadVideoUrl(url, limit = 5 * 1024 * 1024) {
    if (!url || _preloadedUrls.has(url)) return;
    _preloadedUrls.add(url);
    _preloadQueue.push({ url, limit });
    _drainPreloadQueue();
  }

  function _drainPreloadQueue() {
    if (_preloadBusy || !_preloadQueue.length) return;
    const { url, limit } = _preloadQueue.shift();
    _preloadBusy = true;
    const ctrl = new AbortController();
    fetch(url, { signal: ctrl.signal })
      .then(res => {
        const reader = res.body.getReader();
        let bytes = 0;
        (function pump() {
          reader.read().then(({ done, value }) => {
            if (done) { _preloadBusy = false; _drainPreloadQueue(); return; }
            bytes += value.length;
            if (limit && bytes >= limit) { ctrl.abort(); _preloadBusy = false; _drainPreloadQueue(); return; }
            pump();
          }).catch(() => { _preloadBusy = false; _drainPreloadQueue(); });
        })();
      })
      .catch(() => { _preloadBusy = false; _drainPreloadQueue(); });
  }


  function activateItem(index) {
    state.currentIdx = index;

    const videoData = state.videos[index];
    if (!videoData) return;

    const itemEl = getItemEl(index);

    // Move the shared player into the active item (insertBefore poster so poster
    // stays on top while the new src buffers, then onPlaying removes it)
    if (itemEl && !itemEl.contains(videoPlayer)) {
      itemEl.insertBefore(videoPlayer, itemEl.firstChild);
    }

    // Swap src — muted is intentionally NOT reset here.
    // If the user already unmuted (gesture), videoPlayer.muted stays false and iOS
    // honours that authorization on the same element across src changes.
    videoPlayer.src = videoData.videoUrl;
    videoPlayer.load();

    // After 3s still paused: show tap-to-play button (no spinner — poster carries the wait)
    setTimeout(() => {
      if (state.currentIdx !== index || !videoPlayer.paused) return;
      const el = getItemEl(index);
      if (!el || el.querySelector('.reel-play-fallback')) return;
      const btn = document.createElement('div');
      btn.className = 'reel-play-fallback';
      btn.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:3;cursor:pointer';
      btn.innerHTML = '<svg viewBox="0 0 24 24" fill="white" width="72" height="72" style="filter:drop-shadow(0 2px 8px rgba(0,0,0,0.5))"><circle cx="12" cy="12" r="12" fill="rgba(0,0,0,0.4)"/><path d="M9.5 7.5l7 4.5-7 4.5z"/></svg>';
      btn.addEventListener('click', () => {
        videoPlayer.addEventListener('playing', () => {
          getItemEl(index)?.querySelector('.reel-play-fallback')?.remove();
        }, { once: true });
        videoPlayer.play().catch(() => {});
      });
      el.appendChild(btn);
    }, 3000);

    videoPlayer.play().catch(() => {
      const onCanPlay = function () {
        if (state.currentIdx !== index) return;
        videoPlayer.play().catch(() => {});
      };
      videoPlayer.addEventListener('canplay', onCanPlay, { once: true });
      setTimeout(() => videoPlayer.removeEventListener('canplay', onCanPlay), 6000);
    });

    // Persistent playing handler — clears poster when frames render, then starts preload
    let tracked = false;
    const onPlaying = function() {
      if (state.currentIdx !== index) { videoPlayer.removeEventListener('playing', onPlaying); return; }
      const el = getItemEl(index);
      el?.querySelector('.reel-poster')?.remove();
      el?.querySelector('.reel-play-fallback')?.remove();
      if (!tracked) {
        tracked = true;
        const v = state.videos[index];
        if (v) trackEvent('reel_video_view', {
          video_id:       v.id,
          product_handle: v.product.handle,
          product_title:  v.product.title,
          brand:          v.brand.name,
          product_type:   v.productType || '',
        });
        // Preload next videos only after current is playing — avoids bandwidth competition
        for (let i = index + 1; i <= index + PRELOAD_AHEAD; i++) {
          const v2 = state.videos[i];
          if (v2?.videoUrl) preloadVideoUrl(v2.videoUrl);
        }
      }
    };
    videoPlayer.addEventListener('playing', onPlaying);

    videoPlayer.addEventListener('canplay', () => {
      if (state.currentIdx === index && videoPlayer.paused) videoPlayer.play().catch(() => {});
    }, { once: true });

    if (state.videos[index]) {
      recordBrandView(state.videos[index].brand.name);
    }

    const remaining = state.videos.length - 1 - index;
    if (remaining <= FETCH_AHEAD && !state.exhausted) {
      if (!state.loading) fetchBatch(); // start fetching early for performance
    }
    if (remaining === 0 && !state.exhausted) {
      showBottomLoader(); // show loader page only when user reaches the last video
    }
  }

  // ─── Scroll ───────────────────────────────────────────────────────────────
  let scrollTicking = false;
  container.addEventListener('scroll', () => {
    if (!scrollTicking) {
      requestAnimationFrame(() => {
        const idx = Math.round(container.scrollTop / container.clientHeight);
        if (idx !== state.currentIdx && idx < state.videos.length) activateItem(idx);
        scrollTicking = false;
      });
      scrollTicking = true;
    }
  }, { passive: true });

  // Restart current video when page becomes visible again (tab switch, lock screen, etc.)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') { videoPlayer.pause(); return; }
    if (pdpSheet.classList.contains('is-open')) return;
    if (videoPlayer.paused) videoPlayer.play().catch(() => {});
  });

  // ─── Tap handler ──────────────────────────────────────────────────────────
  container.addEventListener('click', function (e) {
    // Tapping the reel behind an open sheet closes it
    if (pdpSheet.classList.contains('is-open')) { closePdpSheet(); return; }

    const btn  = e.target.closest('[data-action]');
    const item = e.target.closest('.reels-item');
    if (!item) return;

    if (btn) {
      const action = btn.dataset.action;

      if (action === 'mute') {
        const nowMuted = !state.muted;
        setMuted(nowMuted);
        // Update this button's label
        const lbl = btn.querySelector('span');
        if (lbl) lbl.textContent = nowMuted ? 'Unmute' : 'Mute';
        return;
      }

      if (action === 'share') { handleShare(item); return; }

      if (action === 'brand') {
        window.location.href = state.videos[item.dataset.index]?.brand?.url
          || `/collections/vendors?q=${encodeURIComponent(item.dataset.brand || '')}`;
        return;
      }

      if (action === 'shop') {
        openPdpSheet(item.dataset.handle, item.dataset.shopifyProductId);
        return;
      }
    }

    // Tap on video area → pause/play (skip if PDP sheet is open)
    if (document.getElementById('reels-pdp-sheet')?.classList.contains('is-open')) return;
    const flash = item.querySelector('.reels-play-flash');

    if (videoPlayer.paused) {
      videoPlayer.play().catch(() => {});
      if (flash) {
        flash.innerHTML = `<svg viewBox="0 0 24 24" fill="white" width="68" height="68"><path d="M8 5v14l11-7z"/></svg>`;
        triggerFlash(flash);
      }
    } else {
      videoPlayer.pause();
      if (flash) {
        flash.innerHTML = `<svg viewBox="0 0 24 24" fill="white" width="68" height="68"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>`;
        triggerFlash(flash);
      }
    }
  });

  function triggerFlash(el) {
    el.classList.remove('is-flashing');
    void el.offsetWidth;
    el.classList.add('is-flashing');
    el.addEventListener('animationend', () => el.classList.remove('is-flashing'), { once: true });
  }

  function handleShare(item) {
    const idx   = parseInt(item.dataset.index, 10);
    const video = state.videos[idx];
    if (!video) return;
    const url = window.location.origin + video.product.url;
    if (navigator.share) {
      navigator.share({ title: video.product.title, url }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(url).then(() => showToast('Link copied!'));
    }
  }



  // ─── Zoom open handler ───────────────────────────────────────────────────
  // activeCzm tracks the CZM currently wired for the open product.
  // Using a module var avoids stale getElementById results when two CZMs share the same ID.
  let activeCzm = null;

  // Popup modals (size chart, view more) moved to body so position:fixed works outside the
  // transform-animated sheet panel. Tracked here for cleanup on close.
  let activePopupModals = [];
  let atcBtnObserver = null;
  let atcInProgress  = false;

  // Capture-phase so it fires before the custom-zoom-modal.js IIFE handler.
  // Delegates to czm._reelsOpen() which is wired per-product in initCzmHandlers.
  // Video media items (deferred-media) are also intercepted here — they open the
  // zoom modal and autoplay instead of loading inline.
  document.addEventListener('click', function(e) {
    if (!pdpSheet.classList.contains('is-open')) return;
    // Let clicks inside an already-open zoom modal fall through to our wired handlers
    const czm = activeCzm;
    if (czm && czm.classList.contains('is-open') && czm.contains(e.target)) return;

    const trigger = e.target.closest('[data-media-id]');
    if (!trigger) return;
    if (!czm || !czm._reelsOpen) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const rawId    = String(trigger.dataset.mediaId);
    const media    = window.__czmMedia || [];
    let idx = 0;
    // Match the FULL data-media-id against __czmMedia ids. Both are section-prefixed:
    // "{section}-{mediaId}" for images, "{section}-tzv-{position}" for videos. The old
    // last-'-'-segment approach never matched videos (id ends in the bare position) and,
    // once the zoom-modal ids were section-prefixed, stopped matching images too.
    const fi = media.findIndex(m => String(m.id) === rawId);
    if (fi >= 0) idx = fi;

    const m = media[idx];
    const isVid = m && (m.type === 'video' || m.type === 'external_video');
    czm._reelsOpen(idx, isVid);
  }, true);

  // ─── Zoom modal controller ────────────────────────────────────────────────
  // Called on every openPdpSheet with the freshly-fetched CZM element.
  // Fully owns modal interactions so the custom-zoom-modal.js IIFE's stale
  // references and stale media[] don't interfere across products.
  function initCzmHandlers(czm) {
    const media  = window.__czmMedia || [];
    let czmIdx   = 0;
    // Zoom state — translate+scale applied to #CzmImage
    let zScale   = 1;
    let zTx      = 0, zTy = 0;
    let dragStartX = 0, dragStartY = 0, dragging = false;

    const czmMain = czm.querySelector('#CzmMain');
    const czmImg  = czm.querySelector('#CzmImage');

    // Spinner overlay injected once into #CzmMain
    const czmSpinner = document.createElement('div');
    czmSpinner.style.cssText = 'position:absolute;inset:0;display:none;align-items:center;justify-content:center;z-index:10;pointer-events:none';
    czmSpinner.innerHTML = '<div style="width:36px;height:36px;border:3px solid rgba(255,255,255,0.25);border-top-color:#fff;border-radius:50%;animation:czm-spin 0.7s linear infinite"></div>';
    if (!document.getElementById('czm-spin-style')) {
      const ks = document.createElement('style');
      ks.id = 'czm-spin-style';
      ks.textContent = '@keyframes czm-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(ks);
    }
    if (czmMain) czmMain.appendChild(czmSpinner);

    function applyZoom() {
      if (!czmImg) return;
      if (zScale === 1) {
        czmImg.style.transform = '';
        czm.classList.remove('czm--zoomed');
      } else {
        czmImg.style.transform = `translate(${zTx}px,${zTy}px) scale(${zScale})`;
        czm.classList.add('czm--zoomed');
      }
    }

    function clampZoom() {
      if (!czmImg || !czmMain) return;
      const iW = parseFloat(czmImg.style.width)  || czmMain.clientWidth;
      const iH = parseFloat(czmImg.style.height) || czmMain.clientHeight;
      const maxX = Math.max(0, (iW * zScale - czmMain.clientWidth)  / 2);
      const maxY = Math.max(0, (iH * zScale - czmMain.clientHeight) / 2);
      zTx = Math.max(-maxX, Math.min(maxX, zTx));
      zTy = Math.max(-maxY, Math.min(maxY, zTy));
    }

    function resetZoom() {
      zScale = 1; zTx = 0; zTy = 0;
      applyZoom();
    }

    function playVideoInCzm() {
      const src = czm._videoSrc;
      if (!src) return;
      const pb  = czm.querySelector('#CzmVideoPosterBtn');
      const vid = czm.querySelector('#CzmVideo');
      if (pb) pb.style.display = 'none';
      if (vid) { vid.style.display = 'block'; vid.src = src; vid.play().catch(() => {}); }
    }

    function showMedia(idx) {
      czmIdx = idx;
      const m   = media[idx];
      if (!m) return;
      const isVid = m.type === 'video' || m.type === 'external_video';

      resetZoom();

      // Highlight active thumb
      czm.querySelectorAll('.czm__thumb').forEach((t, i) => {
        t.classList.toggle('is-active', i === idx);
        t.setAttribute('aria-pressed', i === idx ? 'true' : 'false');
      });

      if (czmImg) {
        czmImg.style.display = isVid ? 'none' : 'block';
        if (!isVid) {
          czmImg.style.width = ''; czmImg.style.height = '';
          czmImg.style.left  = ''; czmImg.style.top    = '';
          czmImg.alt = m.alt || '';
          czmImg.onload = function() {
            czmSpinner.style.display = 'none';
            if (!czmMain) return;
            const cW = czmMain.clientWidth, cH = czmMain.clientHeight;
            if (!cW || !cH || !czmImg.naturalWidth) return;
            const r  = czmImg.naturalWidth / czmImg.naturalHeight, cr = cW / cH;
            const w  = r > cr ? cW : cH * r, h = r > cr ? cW / r : cH;
            czmImg.style.width = w + 'px'; czmImg.style.height = h + 'px';
            czmImg.style.left  = ((cW - w) / 2) + 'px';
            czmImg.style.top   = ((cH - h) / 2) + 'px';
          };
          if (czmImg.src !== m.src) {
            czmSpinner.style.display = 'flex';
            czmImg.src = m.src;
          } else if (czmImg.complete && czmImg.naturalWidth) {
            czmImg.onload();
          }
        }
      }

      const czmVW  = czm.querySelector('#CzmVideoWrap');
      const czmVid = czm.querySelector('#CzmVideo');
      const czmPB  = czm.querySelector('#CzmVideoPosterBtn');
      if (czmVW) {
        czmVW.style.display = isVid ? 'flex' : 'none';
        if (isVid) {
          if (czmVid) { czmVid.pause(); czmVid.src = ''; czmVid.style.display = 'none'; }
          if (czmPB) czmPB.style.display = 'flex';
          czm._videoSrc = m.videoSrc || null;
          const poster = czm.querySelector('#CzmVideoPoster');
          if (poster) poster.src = m.poster || '';
        }
      }
    }

    // ── Close ──
    czm.querySelector('#CzmClose')?.addEventListener('click', e => {
      e.stopImmediatePropagation();
      czm.classList.remove('is-open', 'czm--zoomed');
      czm.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      resetZoom();
      const v = czm.querySelector('#CzmVideo');
      if (v) { v.pause(); v.src = ''; }
    });

    // ── Thumbnails ──
    czm.querySelectorAll('.czm__thumb').forEach((t, i) => {
      t.addEventListener('click', e => { e.stopImmediatePropagation(); showMedia(i); });
    });

    // ── Hide the zoom button — zoom is triggered by tapping the image ──
    const czmZoom = czm.querySelector('#CzmZoom');
    if (czmZoom) czmZoom.style.display = 'none';

    // ── Tap image to zoom in/out ──
    if (czmMain) {
      czmMain.addEventListener('click', e => {
        // Skip if clicking on close button or video controls (they stop propagation)
        if (!czmImg || czmImg.style.display === 'none') return; // video mode
        if (zScale === 1) {
          zScale = 2; zTx = 0; zTy = 0;
        } else {
          zScale = 1; zTx = 0; zTy = 0;
        }
        czmImg.style.transition = 'transform 0.25s ease';
        applyZoom();
        setTimeout(() => { czmImg.style.transition = ''; }, 260);
      });
    }

    // ── Touch-drag pan when zoomed ──
    if (czmMain) {
      czmMain.addEventListener('touchstart', e => {
        if (zScale <= 1 || e.touches.length !== 1) return;
        dragging  = true;
        dragStartX = e.touches[0].clientX - zTx;
        dragStartY = e.touches[0].clientY - zTy;
      }, { passive: true });

      czmMain.addEventListener('touchmove', e => {
        if (!dragging || e.touches.length !== 1) return;
        zTx = e.touches[0].clientX - dragStartX;
        zTy = e.touches[0].clientY - dragStartY;
        clampZoom();
        applyZoom();
        e.preventDefault();
      }, { passive: false });

      czmMain.addEventListener('touchend', () => { dragging = false; });
    }

    // ── Video play button inside zoom modal ──
    czm.querySelector('#CzmVideoPosterBtn')?.addEventListener('click', e => {
      e.stopImmediatePropagation();
      playVideoInCzm();
    });

    // ── Expose open function for the capture handler ──
    // autoplay=true: video media — skip poster, play immediately
    czm._reelsOpen = function(idx, autoplay) {
      resetZoom();
      showMedia(idx);
      czm.classList.remove('czm--zoomed');
      czm.classList.add('is-open');
      czm.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (autoplay) setTimeout(playVideoInCzm, 80);
    };
  }

  // ─── PDP Bottom Sheet (Section Rendering API) ────────────────────────────
  pdpPanel.addEventListener('click', e => e.stopPropagation());
  // Close when tapping outside the panel (overlay area or visible reel behind the sheet)
  pdpSheet.addEventListener('click', function(e) {
    if (!pdpPanel.contains(e.target)) closePdpSheet();
  });
  pdpClose.addEventListener('click', closePdpSheet);

  let touchStartY = 0;
  let touchStartScrollTop = 0;
  pdpPanel.addEventListener('touchstart', e => {
    touchStartY          = e.touches[0].clientY;
    touchStartScrollTop  = pdpContent.scrollTop;
  }, { passive: true });
  pdpPanel.addEventListener('touchend', e => {
    const dy = e.changedTouches[0].clientY - touchStartY;
    // Only close when content was already scrolled to top AND the drag is clearly downward.
    // Prevents fast upward content-scrolls (which also end at scrollTop=0) from
    // accidentally closing — those set a non-zero touchStartScrollTop.
    if (touchStartScrollTop === 0 && dy > 80) closePdpSheet();
  }, { passive: true });

  let sheetOpenedWhilePlaying = false;

  async function openPdpSheet(handle, shopifyProductId) {
    if (!handle) return;
    const _v = state.videos[state.currentIdx];
    trackEvent('reel_pdp_open', {
      product_handle:     handle,
      product_title:      _v?.product?.title  || '',
      brand:              _v?.brand?.name      || '',
      shopify_product_id: shopifyProductId     || '',
    });
    // Pause the active reel; remember whether it was playing so we can resume on close.
    sheetOpenedWhilePlaying = !videoPlayer.paused;
    videoPlayer.pause();

    pdpContent.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;min-height:400px"><svg width="34" height="34" viewBox="0 0 34 34" xmlns="http://www.w3.org/2000/svg"><circle cx="17" cy="17" r="14" fill="none" stroke="rgba(0,0,0,0.12)" stroke-width="3"/><circle cx="17" cy="17" r="14" fill="none" stroke="#333" stroke-width="3" stroke-dasharray="22 66" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" dur="0.7s" from="0 17 17" to="360 17 17" repeatCount="indefinite"/></circle></svg></div>';
    pdpSheet.classList.add('is-open');
    pdpSheet.removeAttribute('aria-hidden');
    document.body.classList.add('reels-sheet-open');
    pdpContent.scrollTop = 0;

    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '?view=reels');
      if (!res.ok) throw new Error('fetch ' + res.status);
      const pageHtml = await res.text();
      const doc = new DOMParser().parseFromString(pageHtml, 'text/html');
      const section = doc.querySelector('[id$="__main"]');
      if (!section) throw new Error('main section not found in page');
      let html = section.innerHTML;

      html = html.replace(/data-update-url="true"/g, 'data-update-url="false"');
      pdpContent.innerHTML = html;

      // Clear Dawn's pre-selected options so user must make an active choice.
      // Exception: auto-select any option group with only one value (e.g. "Free Size") —
      // there's no real choice to make, so requiring a click would block ATC unnecessarily.
      pdpContent.querySelectorAll('variant-selects input[type="radio"]').forEach(r => { r.checked = false; });
      const _vsEl = pdpContent.querySelector('variant-selects');
      if (_vsEl) {
        const _byName = {};
        _vsEl.querySelectorAll('input[type="radio"]').forEach(r => { (_byName[r.name] = _byName[r.name] || []).push(r); });
        Object.values(_byName).forEach(inputs => { if (inputs.length === 1) inputs[0].checked = true; });
      }

      // Immediately hide elements that should not appear in the bottom sheet context.
      // CSS !important rules are in sheetStyle below, but direct style is an extra guarantee
      // against any flash or specificity edge case on mobile.
      pdpContent.querySelectorAll(
        '.product-hero__text-wrapper, #back-button, .product-page-share-btn, .skip-to-content-link'
      ).forEach(el => { el.style.setProperty('display', 'none', 'important'); });

      // Remove product media modal-dialog — it can't upgrade correctly outside .shopify-section
      // and throws "modal.show is not a function" when modal-opener's button is clicked.
      pdpContent.querySelectorAll('[id^="ProductModal-"]').forEach(el => el.remove());

      // Execute inline scripts (browsers skip scripts on innerHTML).
      // Needed so window.__czmMedia is populated with this product's media before initCzmHandlers.
      pdpContent.querySelectorAll('script:not([src])').forEach(s => {
        try { new Function(s.textContent)(); } catch {} // eslint-disable-line no-new-func
      });

      // Move popup modals (size chart, view more) to body so position:fixed works correctly.
      // ModalDialog.connectedCallback crashes on closest('.shopify-section') in our context,
      // so we do the move manually. Setting moved=true causes connectedCallback to bail early.
      activePopupModals.forEach(el => el.remove());
      activePopupModals = [];
      pdpContent.querySelectorAll('.product-popup-modal[id]').forEach(el => {
        el.moved = true;
        document.body.appendChild(el);
        activePopupModals.push(el);
        // Wire close button explicitly (ModalDialog.constructor may not run reliably here)
        el.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => {
          el.removeAttribute('open');
          document.body.classList.remove('overflow-hidden');
        });
        el.addEventListener('click', evt => {
          if (evt.target === el) {
            el.removeAttribute('open');
            document.body.classList.remove('overflow-hidden');
          }
        });
      });

      // Replace body-level CZM with this product's fresh CZM and wire our handlers.
      // We own all CZM interactions — the custom-zoom-modal.js IIFE's handlers on old
      // elements become dead (detached) and never fire for the user.
      // Use activeCzm (not getElementById) to avoid finding the new CZM inside pdpContent.
      const _newCzm = pdpContent.querySelector('#CustomZoomModal');
      if (_newCzm) {
        if (activeCzm && activeCzm !== _newCzm) activeCzm.remove();
        activeCzm = _newCzm;
        document.body.appendChild(_newCzm);
        initCzmHandlers(_newCzm);
      }

      // Keep ATC button enabled (not disabled, not aria-disabled) when no variant is selected.
      // Dawn's product-form.js checks aria-disabled first — if removed, it will run its own
      // missing-option validation and show "Please select an option from X and Y before adding."
      // We must remove BOTH disabled and aria-disabled, then let Dawn handle the rest.
      if (atcBtnObserver) { atcBtnObserver.disconnect(); atcBtnObserver = null; }
      const _atcForm = pdpContent.querySelector('[data-type="add-to-cart-form"]');
      atcBtnObserver = new MutationObserver((mutations) => {
        if (atcInProgress) return;
        for (const m of mutations) {
          const el = m.target;
          if (el.getAttribute('type') !== 'submit') continue;
          const isBlocked = el.disabled || el.getAttribute('aria-disabled') === 'true';
          if (!isBlocked) continue;
          if (!resolveVariantFromSelections()) {
            el.disabled = false;
            el.removeAttribute('aria-disabled');
            el.classList.remove('button--secondary');
            el.classList.add('button--primary');
            const form = el.closest('[data-type="add-to-cart-form"]');
            if (form) form.classList.remove('product-form--hide-errors');
          }
        }
      });
      atcBtnObserver.observe(pdpContent, { subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });

      setTimeout(syncVariantButton, 0);

      // Upgrade any custom elements already defined (2nd+ opens)
      if (typeof customElements !== 'undefined' && customElements.upgrade) {
        customElements.upgrade(pdpContent);
      }

      // Load scripts from the product page that aren't on /pages/reels yet
      const newScriptPromises = [];
      const loadedSrcs = new Set([...document.querySelectorAll('script[src]')].map(s => s.src));
      doc.querySelectorAll('script[src]').forEach(old => {
        if (!loadedSrcs.has(old.src)) {
          const s = document.createElement('script');
          s.src = old.src;
          if (old.type) s.type = old.type; // preserve type="module" for ES module scripts
          newScriptPromises.push(new Promise(r => { s.onload = r; s.onerror = r; }));
          document.head.appendChild(s);
          loadedSrcs.add(old.src);
        }
      });
      if (newScriptPromises.length) {
        Promise.all(newScriptPromises).then(() => {
          if (typeof customElements !== 'undefined' && customElements.upgrade) {
            customElements.upgrade(pdpContent);
          }
          syncVariantButton();
        });
      }

      // Scoped CSS overrides
      const sheetStyle = document.createElement('style');
      sheetStyle.textContent = [
        '.product-hero__text-wrapper { display: none !important; }',
        '#back-button { display: none !important; }',
        '.reels-pdp-sheet__content .skip-to-content-link { display: none !important; }',
        '.reels-pdp-sheet__content .page-width { padding: 0 16px !important; max-width: 100% !important; width: 100% !important; box-sizing: border-box !important; }',
        '.reels-pdp-sheet__content .product.grid { display: block !important; }',
        '.reels-pdp-sheet__content .grid__item { width: 100% !important; max-width: 100% !important; float: none !important; }',
        '.reels-pdp-sheet__content .product__media-wrapper { margin: 0 -16px !important; overflow: hidden !important; }',
        '.reels-pdp-sheet__content .product__media-list { display: flex !important; overflow-x: auto !important; scroll-snap-type: x mandatory !important; -webkit-overflow-scrolling: touch !important; scrollbar-width: none !important; }',
        '.reels-pdp-sheet__content .product__media-list::-webkit-scrollbar { display: none !important; }',
        '.reels-pdp-sheet__content .product__media-item { flex: 0 0 88% !important; scroll-snap-align: start !important; scroll-snap-stop: always !important; }',
        '.reels-pdp-sheet__content .scroll-trigger, .reels-pdp-sheet__content [class*="animate--"] { opacity: 1 !important; transform: none !important; visibility: visible !important; animation: none !important; }',
        // Center the video/3D play badge — on mobile the theme puts it top-right; we want center
        '.reels-pdp-sheet__content .product__modal-opener--video .product__media-icon, .reels-pdp-sheet__content .product__modal-opener--external_video .product__media-icon { top: 50% !important; left: 50% !important; right: auto !important; transform: translate(-50%,-50%) !important; width: 56px !important; height: 56px !important; }',
        // Force button to always show primary theme color when not disabled
        '.reels-pdp-sheet__content [data-type="add-to-cart-form"] [type="submit"]:not([disabled]) { background-color: rgb(var(--color-button)) !important; color: rgb(var(--color-button-text)) !important; border-color: rgb(var(--color-button)) !important; opacity: 1 !important; }',
        // Inline error wrapper — ensure it shows when hidden attribute is removed
        '.reels-pdp-sheet__content .product-form__error-message-wrapper:not([hidden]) { display: block !important; visibility: visible !important; opacity: 1 !important; }',
      ].join(' ');
      pdpContent.prepend(sheetStyle);

      const clearAnimations = () => {
        pdpContent.querySelectorAll('.scroll-trigger, [class*="animate--"]').forEach(el => {
          el.classList.add('is-visible');
          el.style.opacity = '';
          el.style.transform = '';
          el.style.visibility = '';
          el.style.animation = '';
        });
      };
      clearAnimations();
      setTimeout(clearAnimations, 80);
      setTimeout(clearAnimations, 400);

    } catch (err) {
      console.error('[Reels PDP] openPdpSheet failed | handle:', handle, '| shopifyProductId:', shopifyProductId || 'unknown', '| error:', err);
      pdpContent.innerHTML = '<p style="padding:32px;text-align:center;color:#888">Could not load product.</p>';
    }
  }

  // ─── Variant resolution (works without variant-selects upgrading) ──────────
  function resolveVariantFromSelections() {
    const vs = pdpContent.querySelector('variant-selects');
    if (!vs) { console.log('[resolveVariant] no variant-selects'); return null; }
    // Group by radio name attribute — one name per product option, avoids fieldset nesting issues
    const allInputs = [...vs.querySelectorAll('input[type="radio"]')];
    const names = [...new Set(allInputs.map(i => i.getAttribute('name')).filter(Boolean))];
    if (!names.length) { console.log('[resolveVariant] no radio names'); return null; }
    // Use .find() instead of CSS selector — name attributes may have trailing newlines
    // from Liquid rendering and CSS.escape+querySelector fails to match them.
    const selectedValues = names.map(name => {
      const checked = allInputs.find(i => i.getAttribute('name') === name && i.checked);
      return checked ? checked.value.trim() : null;
    });
    console.log('[resolveVariant] names:', names, '| selectedValues:', selectedValues);
    if (selectedValues.some(v => v === null)) return null;
    // Find the first script[type="application/json"] that parses to a non-empty array.
    // Some Dawn components put a single-variant object in variant-selects; skip those.
    const candidateEls = [
      ...vs.querySelectorAll('script[type="application/json"]'),
      pdpContent.querySelector('script[type="application/json"].product-variants-json'),
      ...pdpContent.querySelectorAll('script[type="application/json"]'),
    ].filter(Boolean);

    let variants = null;
    for (const el of candidateEls) {
      try {
        const parsed = JSON.parse(el.textContent);
        if (Array.isArray(parsed) && parsed.length > 0) {
          variants = parsed;
          console.log('[resolveVariant] using JSON from:', el.className || '(no class)', '| variants count:', parsed.length);
          break;
        }
      } catch {}
    }
    if (!variants) { console.log('[resolveVariant] no variants array found'); return null; }

    console.log('[resolveVariant] looking for selectedValues:', JSON.stringify(selectedValues));
    const matched = variants.find(v => {
      const opts = Array.isArray(v.options) && v.options.length
        ? v.options
        : [v.option1, v.option2, v.option3].filter(o => o != null);
      return opts.every((opt, i) => opt === selectedValues[i]);
    });
    console.log('[resolveVariant] result:', matched ? matched.id : null);
    return matched || null;
  }

  function syncVariantButton() {
    const form    = pdpContent.querySelector('[data-type="add-to-cart-form"]');
    const btnEl   = form?.querySelector('[type="submit"]');
    const idInput = form?.querySelector('[name="id"]');
    if (!form || !btnEl || !idInput) return;
    const spanEl = btnEl.querySelector('span') || btnEl;
    btnEl.classList.remove('button--secondary');
    btnEl.classList.add('button--primary');

    const variant = resolveVariantFromSelections();

    if (!variant) {
      // Update button text to "Select [first unselected option]"
      const vsEl = pdpContent.querySelector('variant-selects');
      if (vsEl) {
        const firstMissingFs = [...vsEl.querySelectorAll('fieldset')].find(fs => {
          const radios = [...fs.querySelectorAll('input[type="radio"]')];
          return radios.length > 0 && !radios.some(r => r.checked);
        });
        if (firstMissingFs) {
          const optName = firstMissingFs.querySelector('legend')?.textContent?.trim().split(':')[0].trim() || 'option';
          spanEl.textContent = 'Select ' + optName;
        }
      }
      btnEl.disabled = false;
      btnEl.removeAttribute('aria-disabled');
      return;
    }

    // Full variant selected — clear any inline error and update button
    const _pfEl = form.closest('product-form') || pdpContent;
    const errW  = _pfEl.querySelector('.product-form__error-message-wrapper');
    if (errW) errW.setAttribute('hidden', '');

    idInput.value      = String(variant.id);
    btnEl.disabled     = !variant.available;
    btnEl.removeAttribute('aria-disabled');
    spanEl.textContent = variant.available ? 'Add to cart' : 'Sold out';
  }

  // change fires on the radio input; click on a label also triggers it.
  // Use both + setTimeout on click to handle cases where change doesn't bubble.
  pdpContent.addEventListener('change', e => {
    if (e.target.matches('input[type="radio"]')) {
      console.log('[change] radio changed, name:', e.target.name, '| value:', e.target.value, '| checked:', e.target.checked);
      const _ew = pdpContent.querySelector('.product-form__error-message-wrapper');
      if (_ew) _ew.setAttribute('hidden', '');
      syncVariantButton();
    }
  });
  pdpContent.addEventListener('click', e => {
    if (e.target.closest('label') || e.target.matches('input[type="radio"]')) {
      setTimeout(syncVariantButton, 0);
    }
  });

  // Popup modal delegated handler — opens size chart / view more popups.
  // Bypasses ModalOpener custom element upgrade chain which is unreliable in our context.
  pdpContent.addEventListener('click', function(e) {
    const opener = e.target.closest('[data-modal]');
    if (!opener) return;
    const selector = opener.getAttribute('data-modal');
    if (!selector || !selector.startsWith('#PopupModal-')) return;
    const modal = document.querySelector(selector);
    if (!modal || modal.hasAttribute('open')) return; // already open or not found
    e.stopPropagation();
    modal.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
  });

  // Description "View more / View less" toggle.
  // Must be on pdpContent, NOT document — pdpPanel.stopPropagation() blocks
  // clicks from reaching document, so the inline script's handler never fires.
  pdpContent.addEventListener('click', function(e) {
    const btn = e.target.closest('.toggle-description');
    if (!btn) return;
    const productId = btn.getAttribute('data-product-id');
    if (!productId) return;
    const container = pdpContent.querySelector('#description-' + productId);
    if (!container) return;
    const shortDesc = container.querySelector('.short-desc');
    const fullDesc  = container.querySelector('.full-desc');
    if (!shortDesc || !fullDesc) return;
    const isExpanded = fullDesc.style.display === 'block';
    shortDesc.style.display = isExpanded ? 'block' : 'none';
    fullDesc.style.display  = isExpanded ? 'none'  : 'block';
    btn.setAttribute('aria-expanded', String(!isExpanded));
  });

  // ─── ATC interceptor ──────────────────────────────────────────────────────
  // Intercept add-to-cart form submit in capture phase so it never reaches
  // the browser's native handler (which would navigate to /cart).
  // Handles ATC via AJAX regardless of whether Dawn's product-form element upgraded.
  pdpSheet.addEventListener('submit', async e => {
    const form = e.target;
    if (!form.matches('[data-type="add-to-cart-form"]')) return;
    e.preventDefault();
    e.stopPropagation();
    // Only handle real user-initiated submits — Dawn's product-form.js may fire
    // programmatic submits during upgrade/initialization; those must be ignored.
    if (!e.isTrusted) return;

    const variant = resolveVariantFromSelections();

    // If options not fully selected, show inline error the same way Dawn does on main PDP
    const vsEl = pdpContent.querySelector('variant-selects');
    if (vsEl && !variant) {
      // Find the first unselected option — use legend text for clean label (no suffix)
      const fieldsets = [...vsEl.querySelectorAll('fieldset')];
      const firstMissingFieldset = fieldsets.find(fs => {
        const radios = [...fs.querySelectorAll('input[type="radio"]')];
        return radios.length > 0 && !radios.some(r => r.checked);
      });
      const label = firstMissingFieldset
        ? (firstMissingFieldset.querySelector('legend')?.textContent?.trim().split(':')[0].trim() || 'option')
        : 'all options';
      // Error wrapper is inside <product-form> but is a sibling of <form>, not a child.
      // Must walk up to product-form (or fall back to pdpContent) to find it.
      const _pfEl        = form.closest('product-form') || pdpContent;
      const errorWrapper = _pfEl.querySelector('.product-form__error-message-wrapper');
      const errorSpan    = _pfEl.querySelector('.product-form__error-message');
      if (errorWrapper && errorSpan) {
        errorSpan.textContent = 'Please select ' + label;
        errorWrapper.removeAttribute('hidden');
        errorWrapper.style.display = '';
      }
      return;
    }

    // Hide any previous inline error
    const _pfElHide = form.closest('product-form') || pdpContent;
    const errW = _pfElHide.querySelector('.product-form__error-message-wrapper');
    if (errW) errW.setAttribute('hidden', '');

    const variantId = variant ? String(variant.id) : form.querySelector('[name="id"]')?.value;
    const quantity  = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
    const submitBtn = form.querySelector('[type="submit"]');

    if (!variantId || variantId === '0') {
      showToast('Please select all options');
      return;
    }

    const submitSpan = submitBtn?.querySelector('span') || submitBtn;
    const origText   = submitSpan?.textContent || 'Add to cart';
    atcInProgress = true;
    if (submitBtn) {
      submitBtn.disabled = true;
      if (submitSpan) submitSpan.textContent = 'Adding...';
    }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity }),
      });
      if (!res.ok) throw new Error('ATC ' + res.status);
      showToast('Added to cart!');
      const _av = state.videos[state.currentIdx];
      trackEvent('reel_add_to_cart', {
        product_handle: _av?.product?.handle || '',
        product_title:  _av?.product?.title  || '',
        brand:          _av?.brand?.name     || '',
        variant_id:     variantId,
        quantity,
      });
      updateCartCount();
    } catch {
      showToast('Could not add to cart. Please try again.');
    } finally {
      atcInProgress = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        if (submitSpan) submitSpan.textContent = origText;
      }
    }
  }, { capture: true });

  function closePdpSheet() {
    pdpSheet.classList.remove('is-open');
    pdpSheet.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('reels-sheet-open');
    if (atcBtnObserver) { atcBtnObserver.disconnect(); atcBtnObserver = null; }
    if (activeCzm) { activeCzm.remove(); activeCzm = null; }
    activePopupModals.forEach(el => el.remove());
    activePopupModals = [];
    pdpContent.innerHTML = '';
    // Resume the reel only if it was playing when the sheet opened.
    if (sheetOpenedWhilePlaying) {
      videoPlayer.play().catch(() => {});
    }
    sheetOpenedWhilePlaying = false;
    updateCartCount();
  }

  // ─── Cart count badge ─────────────────────────────────────────────────────
  const cartCountEl = document.getElementById('reels-cart-count');

  async function updateCartCount() {
    if (!cartCountEl) return;
    try {
      const data = await fetch('/cart.js').then(r => r.json());
      const n = data.item_count || 0;
      if (n > 0) {
        cartCountEl.textContent = n > 99 ? '99+' : n;
        cartCountEl.style.display = 'block';
      } else {
        cartCountEl.style.display = 'none';
      }
    } catch {}
  }

  // Dawn fires this event whenever the cart is updated
  document.addEventListener('cart:refresh', updateCartCount);
  // Refresh count when user navigates back to reels page from cart or product
  window.addEventListener('pageshow', updateCartCount);

  // ─── Toast ────────────────────────────────────────────────────────────────
  function showToast(msg) {
    const t = document.createElement('div');
    t.style.cssText = 'position:fixed;bottom:90px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.78);color:#fff;padding:9px 20px;border-radius:22px;font-size:13px;z-index:9999;pointer-events:none;white-space:nowrap;letter-spacing:0.02em';
    t.textContent = msg;
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 2300);
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  function boot() {
    document.body.classList.add('reels-active');
    fetchBatch();
    updateCartCount();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

